import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import {
  getWaterfallCaseYearInstrumentValue,
  getWaterfallCaseInstrumentValue,
  getWaterfallInstrumentValue,
  getWaterfallCaseYearInstrumentTransactionSummary,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { l } from '@locale/setupI18n';
import { FC } from 'react';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import {
  getPvLabel,
  getMvLabel,
  getWeightedAverageMvLabel,
  ValuationSection,
} from './utils/valuationLabelHelper';

export const ValuationInstrumentTotal: FC<{
  instrument: InstrumentDefinitionWithId;
  columnsCount: number;
  interpolationOption: { escapeValue: boolean };
  hasInterims: boolean;
  hasMultipleTranches: boolean;
}> = ({ instrument, columnsCount, interpolationOption, hasInterims, hasMultipleTranches }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const maxDataColspanCount = columnsCount - 1;

  const shouldDisplayDloc = (() => {
    // Check if any case/year has a non-zero dlocPc value for this instrument
    if (!results?.waterfall?.cases) {
      return false;
    }

    return Object.values(results.waterfall.cases).some((caseItem) =>
      Object.values(caseItem.years || {}).some((year) => {
        const instrumentData = year.instruments?.[instrument.instrumentId];
        return instrumentData?.dlocPc != null && instrumentData.dlocPc !== 0;
      })
    );
  })();

  return (
    <>
      <tr>
        <td colSpan={columnsCount} className="table-primary__cell--section-separator" />
      </tr>
      <tr>
        <th className="table-primary__cell--header-tertiary">
          {l('_TotalValueOfName', {
            name: instrument.instrumentNarrative,
            interpolation: interpolationOption,
          })}
        </th>
        <th colSpan={maxDataColspanCount} className="table-primary__cell--header-tertiary" />
      </tr>
      {hasInterims && (
        <TableSectionValuesPerForecast
          title={l('_TotalInterimCashFlows')}
          highlight
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentTransactionSummary(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (transactionSummary) => transactionSummary.totalInterimDistributions,
                true
              )}
              dataTestId={`total-interim-cashflows-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}

      <TableSectionValuesPerForecast
        title={l('_TotalNetExitWeightedProceeds')}
        highlight
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearInstrumentValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (instrument) => instrument.netDistributions
            )}
            dataTestId={`total-net-exit-weighted-proceeds-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      {hasInterims && (
        <TableSectionValuesPerForecast
          title={l('_TotalProceeds')}
          additionalTitleClassNames="table-primary__cell--strong"
          highlight
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => {
            return (
              <CellValue
                {...amountRules}
                className="table-primary__cell--strong"
                value={getWaterfallCaseYearInstrumentValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  instrument.instrumentId,
                  (instrument) => instrument.totalProceeds
                )}
                dataTestId={`total-proceeds-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
              />
            );
          }}
        />
      )}
      <TableSectionValuesPerForecast
        title={l(
          hasMultipleTranches
            ? '_ImpliedDiscountRateAcrossTranches'
            : '_ImpliedDiscountRateAcrossAllProceeds'
        )}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={getCalculatedProjectValueInPercent(
              getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.impliedDiscountRate
              )
            )}
            dataTestId={`implied-discount-rate-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={getPvLabel({
          section: ValuationSection.InstrumentTotal,
          instrument,
          interpolationOption,
        })}
        additionalTitleClassNames="table-primary__cell--strong"
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            strong
            value={getWaterfallCaseYearInstrumentValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (instrument) => instrument.totalExitProceedsFairValue
            )}
            dataTestId={`pv-of-instrument-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <tr>
        <th className="table-primary__cell--strong">{l('_ImpliedMinorityDiscounts')}</th>
        <th className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
      </tr>
      {shouldDisplayDloc && (
        <>
          <TableSectionValuesPerForecast
            title={l('_DLOC')}
            renderSpacer={false}
            renderCell={({ forecast, caseItem }) => (
              <CellValue
                {...enteredPercentageViewRules}
                value={getCalculatedProjectValueInPercent(
                  getWaterfallCaseYearInstrumentValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    (instrument) => instrument.dlocPc
                  )
                )}
                dataTestId={`dloc-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
              />
            )}
          />
        </>
      )}
      <TableSectionValuesPerForecast
        title={l('_DLOM')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => {
          return (
            <CellValue
              {...enteredPercentageViewRules}
              value={getCalculatedProjectValueInPercent(
                getWaterfallCaseYearInstrumentValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  instrument.instrumentId,
                  (instrument) => instrument.dlomPc
                )
              )}
              dataTestId={`dlom-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
      <TableSectionValuesPerForecast
        title={l('_PostDiscountPvOfInstrumentName', {
          instrument: instrument.instrumentNarrative,
          interpolation: interpolationOption,
        })}
        renderCell={({ forecast, caseItem }) => {
          return (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.totalExitProceedsDiscountedFairValue
              )}
              dataTestId={`post-discount-pv-of-instrument-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
      <TableSectionValuesPerCase
        renderSpacer={false}
        title={getMvLabel({
          section: ValuationSection.InstrumentTotal,
          instrument,
          interpolationOption,
        })}
        renderCell={({ caseItem }) => {
          return (
            <CellValue
              {...amountRules}
              alignment={Alignment.Center}
              value={getWaterfallCaseInstrumentValue(
                results,
                caseItem.caseId,
                instrument.instrumentId,
                (instrument) => instrument.weightedMarketValue
              )}
              dataTestId={`mv-of-instrument-cell-${caseItem.caseId}-${instrument.instrumentId}`}
            />
          );
        }}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <tr>
        <th className="table-primary__cell--vertical-separator table-primary__cell--background-secondary table-primary__cell--strong">
          {getWeightedAverageMvLabel({
            section: ValuationSection.InstrumentTotal,
            instrument,
            interpolationOption,
          })}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--background-secondary" />
        <td colSpan={columnsCount - 2} className="table-primary__cell--background-secondary">
          <CellValue
            {...amountRules}
            strong
            alignment={Alignment.Center}
            value={getWaterfallInstrumentValue(
              results,
              instrument.instrumentId,
              (instrument) => instrument.weightedDiscountedFairValue
            )}
            dataTestId={`weighted-average-market-value-cell-${instrument.instrumentId}`}
          />
        </td>
      </tr>
    </>
  );
};
